import { ChevronRightIcon, DocumentIcon } from '@heroicons/react/24/outline';
import { InvestorRightEnumType } from '__generated__/graphql';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { FC } from 'react';

import Badge from 'primitives/Badge';

import investorRightToReadable from 'utils/enums/investor-right-to-readable';
import statusEnumToReadable from 'utils/enums/status-enum-to-readable';
import { formatCurrency, formatNumber } from 'utils/format-helper';

const ShareAssetCard: FC<{
  id: string;
  status: string;
  createdAt: string;
  numberOfShares: number;
  issueNumberOfShares: number;
  investorRights?: InvestorRightEnumType[];
  documents?: {
    id: string;
    name: string;
    url: string;
  }[];
  shareClass: {
    id: string;
    name: string;
    issueDate: string;
    issuePricePerShare: number;
    latestPricePerShare: number;
  };
  onClick: (id: string) => void;
}> = ({
  id,
  status,
  createdAt,
  numberOfShares,
  issueNumberOfShares,
  investorRights,
  documents,
  shareClass,
  onClick,
}) => {
  const { label: statusLabel, color: statusColor } = statusEnumToReadable(status);

  function lineItem(label: string, value: string, color: string = 'text-gray-800') {
    return (
      <div className="my-0.5 gap-x-2 grid grid-cols-2">
        <span className="text-sm leading-5 text-gray-600">{label}:</span>
        <span className={classNames('text-sm font-semibold leading-5', color)}>{value}</span>
      </div>
    );
  }

  const priceMovement =
    Math.round((shareClass.latestPricePerShare - shareClass.issuePricePerShare) * 100) / 100;

  return (
    <li
      className="relative flex justify-between items-start gap-x-6 px-4 py-3 hover:bg-gray-50 cursor-pointer"
      onClick={() => onClick(id)}
    >
      <div className="min-w-0 flex-auto">
        <Badge label="Share Purchase" color="indigo" />
        <p className="mt-1 font-semibold leading-6 text-indigo-700">
          <span className="text-sm font-medium text-gray-700">Share Class: </span>
          {shareClass.name}
        </p>
        <div className="grid grid-cols-2 gap-x-4 mt-2">
          {lineItem('Issue Date', dayjs(shareClass.issueDate).format('DD MMMM YYYY'))}
          {lineItem('Issue Number of Shares', formatNumber(issueNumberOfShares))}
          {lineItem('Current Number of Shares', formatNumber(numberOfShares))}
          {lineItem('Issue Price Per Share', formatCurrency(shareClass.issuePricePerShare))}
          {lineItem(
            'Issue Value',
            formatCurrency(shareClass.issuePricePerShare * issueNumberOfShares)
          )}
          {lineItem(
            'Latest Value Per Share',
            formatCurrency(shareClass.latestPricePerShare),
            priceMovement > 0
              ? 'text-green-500'
              : priceMovement < 0
              ? 'text-red-500'
              : 'text-gray-800'
          )}
          {lineItem(
            'Latest Value',
            formatCurrency(shareClass.latestPricePerShare * numberOfShares),
            priceMovement > 0
              ? 'text-green-500'
              : priceMovement < 0
              ? 'text-red-500'
              : 'text-gray-800'
          )}
        </div>
        {investorRights && investorRights.length > 0 && (
          <div className="mt-4">
            <p className="text-sm font-medium text-gray-700 mb-2">Investor Rights:</p>
            <div className="flex flex-wrap gap-2">
              {investorRights.map((right, index) => (
                <Badge key={index} {...investorRightToReadable(right)} size="sm" />
              ))}
            </div>
          </div>
        )}
        {documents && documents.length > 0 && (
          <div className="mt-4">
            <p className="text-sm font-medium text-gray-700 mb-2">Documents:</p>
            <div className="flex flex-wrap gap-2">
              {documents.map(doc => (
                <a
                  key={doc.id}
                  href={doc.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-sm text-indigo-600 hover:text-indigo-800"
                  onClick={e => e.stopPropagation()}
                >
                  <DocumentIcon className="h-4 w-4" />
                  {doc.name}
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-x-4">
        <div className="hidden sm:flex sm:flex-col sm:items-end">
          <Badge label={statusLabel} color={statusColor} />
          <p className="mt-1 text-xs leading-5 text-gray-500">
            Created on{' '}
            <time dateTime={dayjs(createdAt).format('DD MMMM YYYY')}>
              {dayjs(createdAt).format('DD MMMM YYYY')}
            </time>
          </p>
        </div>
        <ChevronRightIcon className="h-5 w-5 flex-none text-gray-400" aria-hidden="true" />
      </div>
    </li>
  );
};

export default ShareAssetCard;
